import { useEffect, useRef, useState } from 'react'
import type ExperienceData from '../../types/experience'
import { getAssetUrl } from '../../utils'
import { formatWithLineBreaks } from '../../utils/formatText'
import Container from '../Container'
import ThrottleGauge from '../animations/ThrottleGauge'
import TrackAnimation from '../animations/TrackAnimation'
import type QuestionData from '../../types/question'
import { recordQuestionResponse, viewQuestion } from '../../api/question'
import type SessionData from '../../types/session'
import OptionButton from '../OptionButton'
import { endSession } from '../../api/session'
import type { CameraControls } from '@react-three/drei'
import * as THREE from 'three'
import quizTransition from '../../utils/quizTransition'
import { gsap } from 'gsap'
import { audio } from '../../utils/audio'
import type { EndPanel, QuizPanel } from '../../types/panel'
import { ASSETS } from '../../assets'

interface Props {
  mode: 'driver' | 'engineer'
  cameraSettings: {
    position: THREE.Vector3
    target: THREE.Vector3
    azimuthRotation?: number
    polarRotation?: number
  }
  session: SessionData
  experience: ExperienceData
  isAnimating: boolean
  controlsRef: React.RefObject<CameraControls>
  onLogoClick?: () => void
  onTransition?: () => void
  onStart?: () => void
  onBackToEducate?: () => void
  onBackToHome?: () => void
  onQuizFinished?: () => void
}
export default function QuizPanel({
  mode,
  cameraSettings,
  session,
  experience,
  isAnimating,
  controlsRef,
  onLogoClick,
  onStart,
  onBackToEducate,
  onBackToHome,
  onQuizFinished,
}: Props) {
  const logo = experience.brandLogo
    ? experience.brandLogo.url.startsWith('blob:')
      ? experience.brandLogo.url
      : getAssetUrl(experience.brandLogo.filename)
    : ASSETS.ibm
  const background = experience.colours?.quizLeftPanelBackground || '#525252'
  const [quizPanelData, setQuizPanelData] = useState<QuizPanel>({} as QuizPanel)
  const [endPanelData, setEndPanelData] = useState<EndPanel>({} as EndPanel)

  useEffect(() => {
    setQuizPanelData(
      mode === 'engineer'
        ? experience.engineerMode.quizPanel?.[0]
        : experience.driverMode.quizPanel?.[0]
    )
    setEndPanelData(
      mode === 'engineer' ? experience.engineerMode.endPanel : experience.driverMode.endPanel
    )
  }, [mode, experience])

  const [quizStage, setQuizStage] = useState<'instruction' | 'ongoing' | 'finished'>('instruction')
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const questions = session.questions as QuestionData[]

  const [option1State, setOption1State] = useState<'default' | 'success' | 'warning' | 'error'>(
    'default'
  )
  const [option2State, setOption2State] = useState<'default' | 'success' | 'warning' | 'error'>(
    'default'
  )
  const [option3State, setOption3State] = useState<'default' | 'success' | 'warning' | 'error'>(
    'default'
  )
  const [allOptionDisabled, setAllOptionDisabled] = useState(false)
  const [incorrectCounter, setIncorrectCounter] = useState(0)

  const [preQuestionAudio, setPreQuestionAudio] = useState<string | null>(null)
  const [correctAudio, setCorrectAudio] = useState<string | null>(null)
  const [incorrectAudio, setIncorrectAudio] = useState<string | null>(null)

  useEffect(() => {
    if (!questions[currentQuestion]?.audios) return
    if (questions[currentQuestion]?.audios.preQuestion?.filename)
      setPreQuestionAudio(
        questions[currentQuestion]?.audios.preQuestion.url.startsWith('data:')
          ? questions[currentQuestion]?.audios.preQuestion.url
          : getAssetUrl(questions[currentQuestion]?.audios.preQuestion.filename)
      )
    if (questions[currentQuestion]?.audios.correctAnswer?.filename)
      setCorrectAudio(
        questions[currentQuestion]?.audios.correctAnswer.url.startsWith('data:')
          ? questions[currentQuestion]?.audios.correctAnswer.url
          : getAssetUrl(questions[currentQuestion]?.audios.correctAnswer.filename)
      )
    if (questions[currentQuestion]?.audios.incorrectAnswer?.filename)
      setIncorrectAudio(
        questions[currentQuestion]?.audios.incorrectAnswer.url.startsWith('data:')
          ? questions[currentQuestion]?.audios.incorrectAnswer.url
          : getAssetUrl(questions[currentQuestion]?.audios.incorrectAnswer.filename)
      )
  }, [questions, currentQuestion])

  useEffect(() => {
    if (!questions[currentQuestion]?.audios || quizStage !== 'ongoing') return

    audio.register({
      load: preQuestionAudio,
      correct: correctAudio,
      incorrect: incorrectAudio,
    })

    setAllOptionDisabled(true)
    audio.play('load', { interrupt: true, onComplete: () => setAllOptionDisabled(false) })
    return () => {
      audio.unload()
    }
  }, [questions, currentQuestion, preQuestionAudio, correctAudio, incorrectAudio, quizStage])

  const qaRef = useRef<HTMLDivElement>(null)
  const [quizAnimationState, setQuizAnimationState] = useState<'completed' | 'animating'>(
    'completed'
  )
  // gsap.registerPlugin(useGSAP)
  // useGSAP(
  //   () => {
  //     if (!qaRef.current) return
  //     gsap.to(qaRef.current, { autoAlpha: 1, y: 0, duration: 0.3, ease: 'power2.out' })
  //   },
  //   { dependencies: [currentQuestion] }
  // )

  const handleBackToEducate = () => {
    if (isAnimating || quizAnimationState === 'animating' || allOptionDisabled) return
    setQuizStage('instruction')
    setCurrentQuestion(0)
    setDefaultOptions()
    onBackToEducate?.()
  }

  const handleBackToHome = () => {
    if (quizStage !== 'finished') return
    onBackToHome?.()
  }

  const startQuiz = () => {
    setQuizStage('ongoing')
    // setAllOptionDisabled(true)
    audio.play('load', { interrupt: true, onComplete: () => setAllOptionDisabled(false) })
    onStart?.()
  }

  const setDefaultOptions = () => {
    setOption1State('default')
    setOption2State('default')
    setOption3State('default')
    setIncorrectCounter(0)
  }

  // const nextQuestion = async () => {
  //   gsap.to(qaRef.current, {
  //     autoAlpha: 0,
  //     y: -12,
  //     duration: 0.5,
  //     ease: 'power2.out',
  //     onStart: () => {
  //       setDefaultOptions()
  //       if (currentQuestion >= questions.length - 1) {
  //         setQuizStage('finished')

  //         onQuizFinished?.()
  //         if (session && session.id) {
  //           try {
  //             endSession(session.id)
  //             // setSession(null)
  //           } catch (error) {
  //             console.error('Error ending session:', error)
  //           }
  //         }

  //         return
  //       }
  //     },
  //     onComplete: () => {
  //       setCurrentQuestion(currentQuestion + 1)
  //       try {
  //         viewQuestion(questions[currentQuestion].id, 1)
  //       } catch (error) {
  //         console.error('Error recoding question response:', error)
  //       }
  //       gsap.to(qaRef.current, { autoAlpha: 1, y: 0, duration: 1, ease: 'power2.in' })
  //     },
  //   })
  // }

  const finishQuiz = async () => {
    if (currentQuestion >= questions.length - 1) {
      setQuizStage('finished')

      onQuizFinished?.()
      if (session && session.id) {
        try {
          endSession(session.id)
          // setSession(null)
        } catch (error) {
          console.error('Error ending session:', error)
        }
      }

      return
    }
  }

  const nextQuestion = async () => {
    if (currentQuestion >= questions.length - 1) {
      await finishQuiz()
      return
    }
    setCurrentQuestion(currentQuestion + 1)
    try {
      viewQuestion(questions[currentQuestion].id, 1)
    } catch (error) {
      console.error('Error recoding question response:', error)
    }
    gsap.to(qaRef.current, { autoAlpha: 1, y: 0, duration: 1, ease: 'power2.in' })
  }

  const updateOptionState = (key: string, state: 'default' | 'success' | 'warning' | 'error') => {
    switch (key) {
      case 'option1':
        setOption1State(state)
        break
      case 'option2':
        setOption2State(state)
        break
      case 'option3':
        setOption3State(state)
        break
      default:
        break
    }
  }

  const answer = async (key: string, answer: string) => {
    const question = questions?.[currentQuestion]
    if (!question) return

    if (session.id) {
      try {
        recordQuestionResponse(session.id, question.id, answer)
      } catch (error) {
        console.error('Error recoding question response:', error)
      }
    }
    if (question.answers.correctAnswer === key) {
      updateOptionState(key, 'success')
      setAllOptionDisabled(true)
      audio.play('correct', {
        interrupt: true,
        onComplete: async () => {
          if (currentQuestion >= questions.length - 1) {
            await nextQuestion()
            return
          }
          const ctrl = controlsRef.current
          if (!ctrl) return

          quizTransition(
            ctrl,
            cameraSettings.target,
            cameraSettings.position,
            currentQuestion % 2 === 0 ? 'leftToRight' : 'downToUp',
            // 'leftToRight',
            () => {
              setAllOptionDisabled(true)
            },
            () => {
              setTimeout(async () => {
                gsap.to(qaRef.current, {
                  autoAlpha: 0,
                  y: -12,
                  duration: 0.5,
                  ease: 'power2.out',
                  onComplete: () => {
                    setDefaultOptions()
                  },
                })
                // await finishQuiz()
              }, 600)
            },
            async () => {
              await nextQuestion()
            },
            () => {},
            setQuizAnimationState
          )
        },
      })
    } else {
      audio.play('incorrect', { interrupt: true })
      if (incorrectCounter === 0) {
        setIncorrectCounter(incorrectCounter + 1)
        updateOptionState(key, 'error')
      } else {
        updateOptionState(key, 'warning')
      }
    }
  }

  return (
    <>
      {quizStage === 'finished' && (
        <div className="absolute top-0 left-0 z-20 h-full w-full" onClick={handleBackToHome}></div>
      )}
      <div className="absolute top-0 left-0 z-0 h-full w-full">
        <Container>
          <div className="absolute inset-0 flex h-full w-full gap-84 py-[61px] pl-[66px]">
            <div
              className="flex h-full w-[537px] shrink-0 flex-col bg-[#525252] px-10 py-[45.87px]"
              style={{ background: `${background}` }}
            >
              <div
                className={`flex items-start justify-between pb-16 ${quizStage === 'finished' ? '' : 'border-b border-black'}`}
              >
                <img src={logo} width={118} height={44} onClick={onLogoClick} />
                <span className="block max-w-2/5 text-[23px] leading-[30px] font-light">
                  {experience.slogan || 'watsonx. Your AI Race Engineer'}
                </span>
              </div>

              {quizStage === 'finished' && (
                <div className="flex flex-1 flex-col items-center justify-between">
                  <div className="flex flex-col items-start justify-between">
                    <span className="block text-[60px] leading-[60px] font-light">
                      {endPanelData.title || 'Congratulations!'}
                    </span>
                    <p className="mt-35 text-[20px] leading-[28px] font-normal">
                      {formatWithLineBreaks(endPanelData.body || '')}
                    </p>
                  </div>
                  <p className="text-[30px] leading-[36px] font-light">
                    {endPanelData.endInstruction
                      ? formatWithLineBreaks(endPanelData.endInstruction)
                      : ''}
                  </p>
                </div>
              )}
              {quizStage !== 'finished' && (
                <>
                  <div className="flex flex-1 items-start justify-between border-b border-black pt-4">
                    <span className="block text-[30px] font-light">
                      {quizPanelData.quizPanel?.title || 'Laps'}
                    </span>
                    <span className="block text-right text-[250px] leading-[80%] font-thin">
                      {quizStage === 'ongoing' ? currentQuestion + 1 : 0}
                    </span>
                  </div>
                  <div className="flex h-1/2 flex-col items-end justify-center">
                    <div className="flex h-full w-2/5 flex-col items-center justify-center gap-4 py-0">
                      <div className="flex flex-col gap-4">
                        <ThrottleGauge
                          size={100}
                          enableAnimation={quizStage === 'ongoing'}
                          staticPosition={0.35}
                        />
                        <ThrottleGauge
                          size={100}
                          enableAnimation={quizStage === 'ongoing'}
                          staticPosition={0.2}
                        />
                        <ThrottleGauge
                          size={100}
                          enableAnimation={quizStage === 'ongoing'}
                          staticPosition={0.55}
                        />
                      </div>
                      <div className="w-full border-b border-black" />
                      <TrackAnimation enableAnimation={quizStage === 'ongoing'} />
                    </div>
                  </div>
                </>
              )}
            </div>

            {quizStage !== 'finished' && (
              <div className="z-20 flex h-full w-full flex-col items-start justify-start bg-transparent pr-12">
                <div className="flex h-[20%] w-full items-start justify-end">
                  <div
                    className="group hover:bg-blue-tint-3 active:bg-blue-tint-3 bg-blue flex flex-shrink-0 items-center justify-center rounded-full hover:cursor-pointer"
                    style={{ width: 83, height: 83 }}
                    onClick={handleBackToEducate}
                  >
                    <img
                      src={
                        quizPanelData.quizPanel?.backCtaIcon
                          ? quizPanelData.quizPanel?.backCtaIcon?.url.startsWith('blob:')
                            ? quizPanelData.quizPanel?.backCtaIcon?.url
                            : getAssetUrl(quizPanelData.quizPanel?.backCtaIcon?.filename)
                          : ASSETS.iconEmergencyExit
                      }
                      width={46}
                      height={46}
                    />
                  </div>
                </div>

                {quizStage === 'instruction' && (
                  <div className="flex flex-col items-start justify-between gap-20 pr-55">
                    <div className="flex flex-col items-start gap-10">
                      <span className="text-[80px] leading-[85px] font-light">
                        {quizPanelData.quizInstruction?.title ||
                          'Welcome to the watsonx test track.'}
                      </span>
                      <span className="w-4/5 text-[36px] leading-[46px] font-bold">
                        {quizPanelData.quizInstruction?.subtitle ||
                          'Time to master your vehicle and optimise your performance!'}
                      </span>
                    </div>
                    <p className="mt-10 w-[82%] text-[28px] leading-[40px] font-normal">
                      {formatWithLineBreaks(quizPanelData.quizInstruction?.body || '') ||
                        'Answer all the questions to complete the laps. \n Keep an eye on the colour prompts to make sure your car is performing at its peak.'}
                    </p>
                    <button
                      className="hover:bg-blue-tint-5 hover:text-blue active:bg-blue-tint-5 active:text-blue bg-blue-tint-3 flex w-[222px] items-center justify-between px-6 py-3 text-left text-[20px] leading-[27px] font-light hover:cursor-pointer"
                      onClick={startQuiz}
                    >
                      <span>{quizPanelData.quizInstruction?.startCtaLabel || 'Tap to Start'}</span>
                      <img src={ASSETS.iconArrowRight} width={24} height={15} />
                    </button>
                  </div>
                )}
                {quizStage === 'ongoing' && (
                  <>
                    <div
                      ref={qaRef}
                      className="flex flex-1 flex-col items-start justify-between gap-20 pr-60"
                    >
                      {/* Question */}
                      <p className="mt-10 flex flex-1 flex-col text-[36px] leading-[50px] font-normal tracking-normal">
                        {formatWithLineBreaks(questions[currentQuestion].question)}
                      </p>

                      {/* Options */}
                      <div className="flex shrink-0 flex-col gap-8">
                        <OptionButton
                          type={option1State}
                          label={questions[currentQuestion].answers.option1}
                          onClick={() =>
                            answer('option1', questions[currentQuestion].answers.option1)
                          }
                          isButtonDisabled={allOptionDisabled}
                        />
                        <OptionButton
                          type={option2State}
                          label={questions[currentQuestion].answers.option2}
                          onClick={() =>
                            answer('option2', questions[currentQuestion].answers.option2)
                          }
                          isButtonDisabled={allOptionDisabled}
                        />
                        <OptionButton
                          type={option3State}
                          label={questions[currentQuestion].answers.option3}
                          onClick={() =>
                            answer('option3', questions[currentQuestion].answers.option3)
                          }
                          isButtonDisabled={allOptionDisabled}
                        />
                      </div>
                    </div>
                    <div className="flex h-[20%] w-full items-start justify-end"></div>
                  </>
                )}
              </div>
            )}
          </div>
        </Container>
      </div>
    </>
  )
}
